import React, { useState, useEffect, useContext, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserProvider, Contract, ethers } from 'ethers';
import { AuthContext } from '../AuthContext';
import axios from 'axios';

// Web3Broker Contract ABI
const WEB3BROKER_ABI = [
  // Main trading functions
  "function buyShares(uint256 stockId, uint256 shares) external",
  "function sellShares(uint256 stockId, uint256 shares) external",
  "function approveUSDC(uint256 amount) external",
  // Admin functions
  "function listStock(string memory symbol, string memory name, uint256 pricePerShare) external",
  "function updateStockPrice(uint256 stockId, uint256 newPrice) external",
  "function pauseTrading() external",
  "function unpauseTrading() external",
  // View functions
  "function getUserShares(address user, uint256 stockId) external view returns (uint256)",
  "function getUserTrades(address user) external view returns (tuple(address user, uint256 stockId, uint256 shares, uint256 pricePerShare, uint256 totalCost, uint256 feeAmount, bool isBuy, uint256 timestamp)[])",
  "function getStock(uint256 stockId) external view returns (tuple(string symbol, string name, address tokenAddress, uint256 pricePerShare, uint256 totalShares, uint256 availableShares, bool isActive))",
  "function getAllStocks() external view returns (tuple(string symbol, string name, address tokenAddress, uint256 pricePerShare, uint256 totalShares, uint256 availableShares, bool isActive)[])",
  // Contract constants
  "function REVENUE_WALLET() external view returns (address)",
  "function FEE_PERCENT() external view returns (uint256)",
  "function FEE_DENOMINATOR() external view returns (uint256)",
  "function USDC() external view returns (address)",
  "function nextStockId() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function paused() external view returns (bool)"
];

const WEB3BROKER_CONTRACT_ADDRESS = "0x7CEd49fb448427622A6A794B2e7AB0C7c8Eb4fe5";

// Grok AI Server (Preserved)
const GROK_AI_SERVER = 'https://grok-agent-proxy-web3brokerai.replit.app';

const Web3Broker = () => {
  const { isAuthenticated, ensName, loginWithSIWE, isENSHolder } = useContext(AuthContext) || {};
  const [account, setAccount] = useState(null);
  const [web3brokerContract, setWeb3brokerContract] = useState(null);

  // Contract state
  const [contractData, setContractData] = useState({
    feePercent: 30,
    feeDenominator: 10000,
    revenueWallet: '0x74338Aea95f8e80D64944f3F2adAC4c2e7Da919a',
    usdcAddress: '',
    isPaused: false,
    totalStocks: 5,
    nextStockId: 6
  });

  // Trading state
  const [selectedStockId, setSelectedStockId] = useState(1);
  const [amount, setAmount] = useState('');
  const [isBuy, setIsBuy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Contract data state
  const [contractStocks, setContractStocks] = useState([]);
  const [userTrades, setUserTrades] = useState([]);
  const [userShares, setUserShares] = useState({});

  // Grok AI Chat State (Now managed locally in the component)
  const [chatOpen, setChatOpen] = useState(false);

  // Default stocks from contract
  const DEFAULT_STOCKS = [
    { id: 1, symbol: 'AAPL', name: 'Apple Inc.', price: '150.00' },
    { id: 2, symbol: 'TSLA', name: 'Tesla Inc.', price: '250.00' },
    { id: 3, symbol: 'NVDA', name: 'NVIDIA Corp.', price: '500.00' },
    { id: 4, symbol: 'MSFT', name: 'Microsoft Corp.', price: '350.00' },
    { id: 5, symbol: 'GOOGL', name: 'Alphabet Inc.', price: '120.00' }
  ];

  // Initialize contract and connection
  useEffect(() => {
    const init = async () => {
      if (window.ethereum && isAuthenticated && isENSHolder) {
        try {
          const provider = new BrowserProvider(window.ethereum);
          const accounts = await provider.send("eth_requestAccounts", []);
          setAccount(accounts[0]);

          // Switch to Sepolia if needed
          const network = await provider.getNetwork();
          if (network.chainId !== 11155111n) {
            await provider.send("wallet_switchEthereumChain", [{ chainId: "0xAA36A7" }]);
            await new Promise(resolve => setTimeout(resolve, 4000));
          }

          // Initialize Web3Broker contract
          const signer = await provider.getSigner();
          const contractInstance = new Contract(WEB3BROKER_CONTRACT_ADDRESS, WEB3BROKER_ABI, signer);
          setWeb3brokerContract(contractInstance);

          // Load contract data
          await loadContractData(contractInstance);
          await loadContractStocks(contractInstance);

        } catch (err) {
          setError("Failed to connect to blockchain: " + err.message);
          console.error(err);
        }
      }
    };
    init();
  }, [isAuthenticated, isENSHolder]);

  // Load user data when account changes
  useEffect(() => {
    if (web3brokerContract && account) {
      loadUserShares();
      loadUserTrades();
    }
  }, [web3brokerContract, account]);

  // Contract data loading functions
  const loadContractData = async (contract) => {
    try {
      const [feePercent, feeDenominator, revenueWallet, usdcAddress, isPaused, nextStockId] = await Promise.all([
        contract.FEE_PERCENT(),
        contract.FEE_DENOMINATOR(),
        contract.REVENUE_WALLET(),
        contract.USDC(),
        contract.paused(),
        contract.nextStockId()
      ]);

      setContractData({
        feePercent: Number(feePercent),
        feeDenominator: Number(feeDenominator),
        feePercentDisplay: (Number(feePercent) / Number(feeDenominator) * 100).toFixed(1),
        revenueWallet: revenueWallet,
        usdcAddress: usdcAddress,
        isPaused: isPaused,
        totalStocks: Number(nextStockId) - 1,
        nextStockId: Number(nextStockId)
      });
    } catch (error) {
      console.error('Failed to load contract data:', error);
    }
  };

  const loadContractStocks = async (contract) => {
    try {
      const contractStocks = await contract.getAllStocks();
      const formattedStocks = contractStocks.map((stock, index) => ({
        id: index + 1,
        symbol: stock.symbol,
        name: stock.name,
        tokenAddress: stock.tokenAddress,
        pricePerShare: ethers.formatUnits(stock.pricePerShare, 6),
        totalShares: ethers.formatUnits(stock.totalShares, 18),
        availableShares: ethers.formatUnits(stock.availableShares, 18),
        isActive: stock.isActive
      }));
      setContractStocks(formattedStocks);
    } catch (error) {
      console.error('Failed to load contract stocks:', error);
      // Fallback to default stocks
      setContractStocks(DEFAULT_STOCKS.map(stock => ({
        ...stock,
        tokenAddress: '0x0000000000000000000000000000000000000000',
        pricePerShare: stock.price,
        totalShares: '1000000',
        availableShares: '1000000',
        isActive: true
      })));
    }
  };

  const loadUserShares = async () => {
    try {
      const shares = {};
      for (let stockId = 1; stockId <= contractData.totalStocks; stockId++) {
        const userStockShares = await web3brokerContract.getUserShares(account, stockId);
        shares[stockId] = ethers.formatUnits(userStockShares, 18);
      }
      setUserShares(shares);
    } catch (error) {
      console.error('Failed to load user shares:', error);
    }
  };

  const loadUserTrades = async () => {
    try {
      const tradesData = await web3brokerContract.getUserTrades(account);
      const formattedTrades = tradesData.map((trade, index) => ({
        id: index,
        stockId: Number(trade.stockId),
        symbol: contractStocks.find(s => s.id === Number(trade.stockId))?.symbol || `Stock-${trade.stockId}`,
        shares: ethers.formatUnits(trade.shares, 18),
        pricePerShare: ethers.formatUnits(trade.pricePerShare, 6),
        totalCost: ethers.formatUnits(trade.totalCost, 6),
        feeAmount: ethers.formatUnits(trade.feeAmount, 6),
        isBuy: trade.isBuy,
        timestamp: Number(trade.timestamp),
        date: new Date(Number(trade.timestamp) * 1000).toLocaleDateString()
      }));
      setUserTrades(formattedTrades);
    } catch (error) {
      console.error('Failed to load user trades:', error);
    }
  };

  // Contract trading functions
  const handleContractApproveUSDC = async (amount) => {
    if (!web3brokerContract) return;
    setLoading(true);
    try {
      const amountInUSDC = ethers.parseUnits(amount.toString(), 6);
      const tx = await web3brokerContract.approveUSDC(amountInUSDC);
      alert(`USDC approval submitted! Transaction: ${tx.hash}`);
      await tx.wait();
      alert(`USDC approved successfully! Amount: $${amount}`);
    } catch (error) {
      console.error('USDC approval error:', error);
      setError(`USDC approval failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleContractBuyShares = async () => {
    if (!web3brokerContract || !selectedStockId || !amount) return;
    setLoading(true);
    try {
      const shares = ethers.parseUnits(amount, 18);
      const tx = await web3brokerContract.buyShares(selectedStockId, shares);
      alert(`Buy order submitted! Transaction: ${tx.hash}`);
      await tx.wait();
      alert(`Buy confirmed! Purchased ${amount} shares`);

      // Refresh data
      await loadUserShares();
      await loadUserTrades();
    } catch (error) {
      console.error('Buy error:', error);
      setError(`Buy failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleContractSellShares = async () => {
    if (!web3brokerContract || !selectedStockId || !amount) return;
    setLoading(true);
    try {
      const shares = ethers.parseUnits(amount, 18);
      const tx = await web3brokerContract.sellShares(selectedStockId, shares);
      alert(`Sell order submitted! Transaction: ${tx.hash}`);
      await tx.wait();
      alert(`Sell confirmed! Sold ${amount} shares`);

      // Refresh data
      await loadUserShares();
      await loadUserTrades();
    } catch (error) {
      console.error('Sell error:', error);
      setError(`Sell failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ENS Domain Connection Handler
  const connectWallet = async () => {
    await loginWithSIWE();
  };

  // Show ENS domain connection requirement if not authenticated
  if (!isAuthenticated || !isENSHolder) {
    return (
      <div style={{ textAlign: 'center', color: 'gold', marginTop: '20px' }}>
        <p>Please mint your .xbsys.eth subdomain to access Web3Broker.</p>
        <button
          onClick={connectWallet}
          style={{
            background: 'transparent',
            border: '2px solid gold',
            color: 'gold',
            padding: '15px 30px',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Connect Wallet
        </button>
      </div>
    );
  }

  // Fixed Conservative Chat Field Component
  const ConservativeChatField = () => {
    // Move chat-related state inside the component to prevent parent re-renders
    const [localMessages, setLocalMessages] = useState([]);
    const [localInput, setLocalInput] = useState('');
    const [localAiLoading, setLocalAiLoading] = useState(false);
    const inputRef = useRef(null);

    // Initialize with welcome message
    useEffect(() => {
      if (localMessages.length === 0) {
        setLocalMessages([{
          role: 'assistant',
          displayName: 'Web3Broker.eth',
          content: `Hi, I'm Web3Broker.eth and Grok is my partner. How can I help with asset trading today, ${ensName || '.xbsys.eth'}?`
        }]);
      }
    }, [ensName]);

    const sendMessage = async () => {
      if (!localInput || localAiLoading) return;
      setLocalAiLoading(true);

      const userMessage = {
        role: 'user',
        displayName: ensName || '.xbsys.eth',
        content: localInput
      };
      setLocalMessages((prev) => [...prev, userMessage]);
      const userInput = localInput;
      setLocalInput('');

      try {
        const response = await axios.post(`${GROK_AI_SERVER}/xai/v1/chat/completions`, {
          model: 'grok-4',
          messages: [
            {
              role: 'system',
              content: "You are Web3Broker.eth, an AI advisor for decentralized stock trading. Be lean, simple, truthful, objective, factual, based on the best market research for insightful views on stocks, and somewhat visionary about market trends. Always link to banksystem.co/web3broker for actions."
            },
            ...localMessages,
            userMessage
          ]
        }, {
          headers: {
            'Authorization': 'Bearer xai-iM9Qqn1TDqdFxwmCXALXUE7ZtLJcN054JPAzarlvlQeAS5nlcz4xcAx6SuBnh9iLn2sBJeDdsy6ksSNY',
            'Content-Type': 'application/json'
          }
        });

        const aiReply = {
          role: 'assistant',
          displayName: 'Web3Broker.eth',
          content: response.data.choices[0].message.content
        };
        setLocalMessages((prev) => [...prev, aiReply]);
      } catch (err) {
        setError("AI chat failed: " + (err.response?.status === 403 ? 'Forbidden - Check proxy or xAI account' : err.message));
      } finally {
        setLocalAiLoading(false);
        // Refocus the input after sending
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        sendMessage();
      }
    };

    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: chatOpen ? '400px' : '300px',
        height: chatOpen ? '500px' : '60px',
        background: '#1a1a1a',
        borderRadius: '15px',
        border: '2px solid #FFD700',
        zIndex: 1000,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)'
      }}>
        {/* Chat Header */}
        <div
          style={{
            padding: '15px',
            background: 'linear-gradient(135deg, #FFD700, #ffed4e)',
            color: 'black',
            fontWeight: 'bold',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onClick={() => setChatOpen(!chatOpen)}
        >
          <span>Web3Broker.eth + Grok AI</span>
          <span>{chatOpen ? '−' : '+'}</span>
        </div>

        {/* Chat Messages (only when open) */}
        {chatOpen && (
          <div style={{
            flex: 1,
            height: '350px',
            overflowY: 'auto',
            padding: '15px',
            background: '#0a0a0a'
          }}>
            {localMessages.map((msg, idx) => (
              <div key={idx} style={{
                marginBottom: '15px',
                padding: '10px',
                borderRadius: '10px',
                background: msg.role === 'user' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(0, 255, 136, 0.1)',
                border: `1px solid ${msg.role === 'user' ? '#FFD700' : '#00ff88'}`
              }}>
                <div style={{
                  fontSize: '12px',
                  color: msg.role === 'user' ? '#FFD700' : '#00ff88',
                  marginBottom: '5px',
                  fontWeight: 'bold'
                }}>
                  {msg.displayName}
                </div>
                <div style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.4' }}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chat Input (always visible but compact when closed) */}
        <div style={{
          padding: '10px',
          borderTop: '1px solid #333',
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          <input
            ref={inputRef}
            type="text"
            value={localInput}
            onChange={(e) => setLocalInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={chatOpen ? "Ask about stocks..." : "Message Grok AI..."}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#1a1a1a',
              color: '#FFD700',
              border: '1px solid #FFD700',
              borderRadius: '20px',
              fontSize: '14px',
              minWidth: '0'
            }}
          />
          <button
            onClick={sendMessage}
            disabled={localAiLoading}
            style={{
              padding: '8px 16px',
              background: localAiLoading ? '#444' : '#00ff88',
              color: 'black',
              border: 'none',
              borderRadius: '20px',
              cursor: localAiLoading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}
          >
            {localAiLoading ? '...' : chatOpen ? 'Send' : '➤'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="web3broker-container">
      <Helmet>
        <title>Web3Broker.eth - Tokenized Stock Trading | BankSystem</title>
      </Helmet>

      {/* Gold Card Container - Same as Web3Markets.js */}
      <div style={{
        background: 'black',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 0 20px gold',
        maxWidth: '1200px',
        margin: '20px auto',
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: 'gold', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            Web3Broker.eth - Tokenized Stock Trading
          </h1>
          <p style={{ color: 'gold', fontSize: '16px', marginBottom: '20px' }}>
            Pure Smart Contract Stock Trading with {contractData.feePercentDisplay || '0.3'}% fees
          </p>
        </div>

        {/* ENS Connection Status - Same as Web3Markets */}
        <div style={{
          backgroundColor: '#333',
          padding: '1rem',
          borderRadius: '5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isAuthenticated ? '#00FF00' : '#FF0000',
            }}></div>
            <span style={{ fontWeight: 'bold', color: isAuthenticated ? '#00FF00' : '#FF0000' }}>
              {isAuthenticated ? 'CONNECTED' : 'DISCONNECTED'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'gold', fontSize: '14px' }}>
              {ensName ? `${ensName}` : account ? `${account.substring(0, 6)}...${account.substring(account.length - 4)}` : 'Not connected'}
            </span>
          </div>
        </div>

        {/* Contract Status */}
        {web3brokerContract && (
          <div style={{
            background: 'rgba(255,215,0,0.1)',
            border: '1px solid gold',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <h4 style={{ color: 'gold', margin: '0 0 10px 0' }}>Contract Status</h4>
            <div style={{ fontSize: '12px', color: 'gold' }}>
              <p>Contract: {WEB3BROKER_CONTRACT_ADDRESS.slice(0, 10)}...{WEB3BROKER_CONTRACT_ADDRESS.slice(-8)}</p>
              <p>Fee: {contractData.feePercentDisplay || '0.3'}% | Status: {contractData.isPaused ? 'PAUSED' : 'ACTIVE'}</p>
              <p>Stocks: {contractData.totalStocks} | Network: Sepolia</p>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            color: 'red',
            marginBottom: '20px',
            textAlign: 'center',
            background: 'rgba(255,0,0,0.1)',
            padding: '10px',
            borderRadius: '5px'
          }}>
            {error}
          </div>
        )}

        {/* Stock Selection */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h4 style={{ color: 'gold', marginBottom: '15px', textAlign: 'center' }}>Available Stocks</h4>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
          }}>
            {DEFAULT_STOCKS.map(stock => (
              <div
                key={stock.id}
                style={{
                  background: selectedStockId === stock.id ? 'rgba(255, 215, 0, 0.2)' : 'rgba(26, 26, 46, 0.5)',
                  border: selectedStockId === stock.id ? '2px solid gold' : '1px solid #555',
                  borderRadius: '8px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'center'
                }}
                onClick={() => setSelectedStockId(stock.id)}
              >
                <h4 style={{ color: 'gold', margin: '0 0 8px 0', fontSize: '18px' }}>{stock.symbol}</h4>
                <p style={{ color: 'gold', margin: '0 0 5px 0', fontSize: '14px' }}>{stock.name}</p>
                <p style={{ color: 'gold', margin: '0', fontSize: '16px', fontWeight: 'bold' }}>${stock.price}/share</p>
                {userShares[stock.id] && parseFloat(userShares[stock.id]) > 0 && (
                  <p style={{ color: '#00ff88', margin: '5px 0 0 0', fontSize: '12px' }}>
                    Your Shares: {parseFloat(userShares[stock.id]).toFixed(6)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trading Interface */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
        }}>
          <h4 style={{ color: 'gold', marginBottom: '15px', textAlign: 'center' }}>Trade Stocks</h4>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ color: 'gold', display: 'block', marginBottom: '8px' }}>Share Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter shares (e.g., 1.5)"
              step="0.000001"
              min="0"
              style={{
                width: '100%',
                padding: '12px',
                background: '#222',
                color: 'gold',
                border: '1px solid gold',
                borderRadius: '5px',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
            <button
              onClick={() => handleContractApproveUSDC(1000)}
              disabled={loading}
              style={{
                padding: '12px',
                background: loading ? '#666' : 'gold',
                color: 'black',
                border: 'none',
                borderRadius: '5px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              Approve $1000 USDC
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button
              onClick={handleContractBuyShares}
              disabled={loading || !amount || !selectedStockId}
              style={{
                padding: '12px',
                background: loading ? '#666' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Processing...' : 'Buy Shares'}
            </button>
            <button
              onClick={handleContractSellShares}
              disabled={loading || !amount || !selectedStockId}
              style={{
                padding: '12px',
                background: loading ? '#666' : '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Processing...' : 'Sell Shares'}
            </button>
          </div>

          {/* Trade Preview */}
          {amount && selectedStockId && (
            <div style={{
              background: 'rgba(255,215,0,0.1)',
              border: '1px solid gold',
              borderRadius: '5px',
              padding: '15px',
              marginTop: '15px'
            }}>
              <h5 style={{ color: 'gold', margin: '0 0 10px 0' }}>Trade Preview</h5>
              {(() => {
                const selectedStock = DEFAULT_STOCKS.find(stock => stock.id === selectedStockId);
                if (!selectedStock) return null;

                const sharesInWei = ethers.parseUnits(amount, 18);
                const priceInUSDC = ethers.parseUnits(selectedStock.price, 6);
                const grossCost = (sharesInWei * priceInUSDC) / ethers.parseUnits('1', 18);
                const grossCostFormatted = ethers.formatUnits(grossCost, 6);
                const feeAmount = parseFloat(grossCostFormatted) * 0.003;
                const totalCost = parseFloat(grossCostFormatted) + feeAmount;

                return (
                  <>
                    <p style={{ color: 'gold', fontSize: '12px', margin: '3px 0' }}>
                      <strong>Stock:</strong> {selectedStock.symbol} ({selectedStock.name})
                    </p>
                    <p style={{ color: 'gold', fontSize: '12px', margin: '3px 0' }}>
                      <strong>Shares:</strong> {amount} × ${selectedStock.price} = ${parseFloat(grossCostFormatted).toFixed(2)}
                    </p>
                    <p style={{ color: 'gold', fontSize: '12px', margin: '3px 0' }}>
                      <strong>Fee:</strong> ${feeAmount.toFixed(4)} (0.3%)
                    </p>
                    <p style={{ color: 'gold', fontSize: '12px', margin: '3px 0', fontWeight: 'bold' }}>
                      <strong>Total:</strong> ${totalCost.toFixed(2)} USDC
                    </p>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Trade History */}
        {userTrades.length > 0 && (
          <div style={{
            backgroundColor: '#333',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
          }}>
            <h4 style={{ color: 'gold', marginBottom: '15px', textAlign: 'center' }}>Your Trade History</h4>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {userTrades.map((trade, index) => (
                <div key={index} style={{
                  background: 'rgba(255,215,0,0.1)',
                  padding: '10px',
                  marginBottom: '10px',
                  borderRadius: '5px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <span style={{ color: 'gold', fontWeight: 'bold' }}>{trade.symbol}</span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '10px',
                      background: trade.isBuy ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 68, 68, 0.2)',
                      color: trade.isBuy ? '#00ff88' : '#ff4444',
                      border: `1px solid ${trade.isBuy ? '#00ff88' : '#ff4444'}`
                    }}>
                      {trade.isBuy ? 'BUY' : 'SELL'}
                    </span>
                  </div>
                  <div style={{ color: 'gold', fontSize: '12px' }}>
                    <div>Shares: {parseFloat(trade.shares).toFixed(6)} @ ${parseFloat(trade.pricePerShare).toFixed(2)}</div>
                    <div>Total: ${parseFloat(trade.totalCost).toFixed(2)} | Fee: ${parseFloat(trade.feeAmount).toFixed(4)}</div>
                    <div style={{ fontSize: '10px', color: '#aaa' }}>{trade.date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contract Info Footer */}
        <div style={{
          backgroundColor: '#333',
          padding: '15px',
          borderRadius: '5px',
          textAlign: 'center',
        }}>
          <h4 style={{ color: 'gold', marginBottom: '10px' }}>Contract Information</h4>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Contract: {WEB3BROKER_CONTRACT_ADDRESS}
          </p>
          <p style={{ color: 'gold', margin: '5px 0', fontSize: '12px' }}>
            Trading Fee: {contractData.feePercentDisplay || '0.3'}% | Total Stocks: {contractData.totalStocks}
          </p>
        </div>
      </div>

      {/* Fixed Conservative Chat Field (stays outside the gold card) */}
      <ConservativeChatField />
    </section>
  );
};

export default Web3Broker;
